/* eslint-disable class-methods-use-this */
import Component from '@bythepixel/component-loader';
import { getPromotionAnalytics } from '@Core/analytics/SingletonAnalyticsManager';

export default class PromotionCard extends Component {
  static selector = '[data-comp-promotion-card]';
  static loaderPriority = 'in-view';

  constructor(containerEl) {
    super(containerEl);

    this.promotionAnalytics = null;
    this.hasTrackedImpression = false;
    this.promotionData = this.extractPromotionData();

    this.initializeAnalytics();
    this.initListeners();
  }

  /**
   * Initialize promotion analytics
   */
  async initializeAnalytics() {
    try {
      this.promotionAnalytics = await getPromotionAnalytics();

      // Track impression when component loads and is in view
      if (!this.hasTrackedImpression && this.promotionAnalytics.isReady()) {
        this.trackImpression();
      }
    } catch (error) {
      console.warn('Failed to initialize promotion analytics:', error);
    }
  }

  /**
   * Extract promotion data from DOM attributes
   * @returns {Object}
   */
  extractPromotionData() {
    const container = this.$container;

    return {
      promotion_id: container.dataset.promotionId || '',
      promotion_name: container.dataset.promotionName || '',
      ad_size: this.getAdSize(),
    };
  }

  /**
   * Get the ad size (card variant) from DOM class
   * @returns {string}
   */
  getAdSize() {
    const container = this.$container;
    const classList = Array.from(container.classList);

    // Find the variant class (promotion-card--{variant})
    const variantClass = classList.find((cls) => cls.startsWith('promotion-card--'));

    if (variantClass) {
      return variantClass.replace('promotion-card--', '');
    }

    return 'default';
  }

  /**
   * Initialize event listeners for promotion interactions
   */
  initListeners() {
    // Track clicks on all buttons within the promotion card
    const buttons = this.$container.querySelectorAll(
      'button, a[href], [data-comp-modal-trigger]',
    );

    buttons.forEach((button) => {
      button.addEventListener('click', (event) => {
        this.handleButtonClick(event, button);
      });
    });
  }

  /**
   * Handle button click events
   * @param {Event} event - Click event
   * @param {Element} button - Clicked button element
   */
  handleButtonClick(event, button) {
    const buttonData = this.extractButtonData(button);

    this.trackClick({
      ...this.promotionData,
      button_title: buttonData.title,
      click_category: buttonData.category,
    });
  }

  /**
   * Extract button data for analytics
   * @param {Element} button - Button element
   * @returns {Object}
   */
  extractButtonData(button) {
    let title = '';
    let category = 'unknown';

    // Get button title/text
    if (button.textContent) {
      title = button.textContent.trim();
    } else if (button.getAttribute('aria-label')) {
      title = button.getAttribute('aria-label');
    }

    // Determine click category based on button characteristics
    if (button.classList.contains('promotion-card__mobile-modal-trigger')) {
      category = 'mobile_modal_trigger';
    } else if (button.hasAttribute('data-comp-modal-trigger')) {
      category = 'modal_trigger';
    } else if (button.classList.contains('btn--secondary')) {
      category = 'secondary_cta';
    } else {
      category = 'primary_cta';
    }

    return {
      title,
      category,
    };
  }

  /**
   * Track promotion impression
   */
  trackImpression() {
    if (
      this.hasTrackedImpression
      || !this.promotionAnalytics
      || !this.promotionAnalytics.isReady()
    ) {
      return;
    }

    this.promotionAnalytics.trackPromotionImpression(this.promotionData);
    this.hasTrackedImpression = true;

    console.debug('Tracked promotion impression:', this.promotionData);
  }

  /**
   * Track promotion click
   * @param {Object} clickData - Click event data
   */
  trackClick(clickData) {
    if (!this.promotionAnalytics || !this.promotionAnalytics.isReady()) {
      console.warn('Promotion analytics not ready for click tracking');
      return;
    }

    this.promotionAnalytics.trackPromotionClick(clickData);

    console.debug('Tracked promotion click:', clickData);
  }

  /**
   * Get promotion analytics instance for external use
   * @returns {PromotionAnalytics}
   */
  getPromotionAnalytics() {
    return this.promotionAnalytics;
  }
}
